import { useEffect, useState } from "react";
import api, { apiError, fileUrl } from "../lib/api";
import { toast } from "sonner";
import { Plus, ReceiptText, X, FileUp, Loader2, FileText, Pencil, Store, Building2, Sparkles, Check } from "lucide-react";
import { useSettings } from "../context/SettingsContext";
import { fieldClass as input, emptyRiga, rowTotal, rowCoherent } from "../components/bolle/shared";
import { RigaRow } from "../components/bolle/RigaRow";
import { BollaMeta } from "../components/bolle/BollaMeta";
import { BollaCard } from "../components/bolle/BollaCard";

export default function Bolle() {
  const { text } = useSettings();
  const [bolle, setBolle] = useState([]);
  const [posteggi, setPosteggi] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [prezziMap, setPrezziMap] = useState({});
  const [numero, setNumero] = useState("");
  const [data, setData] = useState("");
  const [posteggioId, setPosteggioId] = useState("");
  const [fornitoreId, setFornitoreId] = useState("");
  const [note, setNote] = useState("");
  const [righe, setRighe] = useState([{ ...emptyRiga }]);
  const [pdfFileId, setPdfFileId] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [meta, setMeta] = useState(null);
  const [prodottiNomi, setProdottiNomi] = useState([]);
  const [learnStats, setLearnStats] = useState(null);
  const [quickPosteggio, setQuickPosteggio] = useState(null); // {nome, settore}
  const [quickFornitore, setQuickFornitore] = useState(null); // {nome, tipo}

  const resetForm = () => {
    setNumero(""); setData(""); setPosteggioId(""); setFornitoreId(""); setNote("");
    setRighe([{ ...emptyRiga }]); setPdfFileId(null); setEditId(null); setMeta(null);
  };
  const startEdit = (b) => {
    setEditId(b.id);
    setNumero(b.numero || "");
    setData((b.data || "").slice(0, 10));
    setPosteggioId(b.posteggio_id || "");
    setFornitoreId(b.fornitore_id || "");
    setNote(b.note || "");
    setPdfFileId(b.pdf_file_id || null);
    setMeta(null);
    setRighe((b.righe || []).map((r) => ({
      prodotto: r.prodotto || "",
      prodotto_originale: r.prodotto_originale || "",
      quantita: r.quantita != null ? String(r.quantita) : "",
      unita: r.unita || "colli",
      prezzo_unitario: r.prezzo_unitario != null ? String(r.prezzo_unitario) : "",
      importo: r.totale != null ? String(r.totale) : (r.importo != null ? String(r.importo) : ""),
      iva: r.iva != null ? String(r.iva) : "",
    })));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const load = async () => {
    const [b, p, f, pr] = await Promise.all([api.get("/bolle"), api.get("/posteggi"), api.get("/fornitori"), api.get("/prodotti")]);
    setBolle(b.data);
    setPosteggi(p.data);
    setFornitori(f.data);
    const map = {};
    (pr.data || []).forEach((x) => { if (x.ultimo_prezzo != null) map[(x.nome || "").trim().toLowerCase()] = x.ultimo_prezzo; });
    setPrezziMap(map);
    setProdottiNomi((pr.data || []).map((x) => x.nome).filter(Boolean));
    api.get("/learning/stats").then((r) => setLearnStats(r.data)).catch((e) => console.debug("learning stats", e));
  };
  useEffect(() => { load().catch((e) => console.error("Bolle load", e)); }, []);

  const createPosteggio = async () => {
    if (!quickPosteggio?.nome?.trim()) { toast.error("Inserisci il nome del posteggio"); return; }
    try {
      await api.post("/posteggi", { nome: quickPosteggio.nome.trim(), settore: quickPosteggio.settore || "" });
      const { data } = await api.get("/posteggi");
      setPosteggi(data);
      const created = data.find((p) => p.nome.toLowerCase() === quickPosteggio.nome.trim().toLowerCase());
      if (created) setPosteggioId(created.id);
      setQuickPosteggio(null);
      toast.success("Posteggio creato e selezionato");
    } catch (err) { toast.error(apiError(err)); }
  };

  const createFornitore = async () => {
    if (!quickFornitore?.nome?.trim()) { toast.error("Inserisci il nome del fornitore"); return; }
    try {
      await api.post("/fornitori", { nome: quickFornitore.nome.trim(), tipo: quickFornitore.tipo || "mercato" });
      const { data } = await api.get("/fornitori");
      setFornitori(data);
      const created = data.find((f) => f.nome.toLowerCase() === quickFornitore.nome.trim().toLowerCase());
      if (created) setFornitoreId(created.id);
      setQuickFornitore(null);
      toast.success("Fornitore creato e selezionato");
    } catch (err) { toast.error(apiError(err)); }
  };

  const setRiga = (i, field, val) => {
    const copy = [...righe];
    copy[i][field] = val;
    setRighe(copy);
  };
  const addRiga = () => setRighe([...righe, { ...emptyRiga }]);
  const removeRiga = (i) => setRighe(righe.filter((_, idx) => idx !== i));

  const priceAnomaly = (r) => {
    const last = prezziMap[(r.prodotto || "").trim().toLowerCase()];
    const pu = parseFloat(r.prezzo_unitario);
    if (last == null || !pu || !last) return null;
    const dev = Math.abs(pu - last) / last;
    return dev > 0.4 ? last : null;
  };

  const totaleNetto = righe.reduce((s, r) => s + rowTotal(r), 0);
  const totaleIva = righe.reduce((s, r) => s + rowTotal(r) * ((parseFloat(r.iva) || 0) / 100), 0);
  const totaleIvato = totaleNetto + totaleIva;

  const parsePdf = async (file) => {
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: d } = await api.post("/bolle/parse-pdf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPdfFileId(d.file_id || null);
      setMeta(d);
      if (d.numero_suggerito && !numero) setNumero(d.numero_suggerito);
      if (d.data_suggerita && !data) setData(d.data_suggerita);
      const parsed = (d.righe || []).map((r) => ({
        prodotto: r.prodotto || "",
        prodotto_originale: r.prodotto_originale || r.prodotto || "",
        quantita: r.quantita != null ? String(r.quantita) : "",
        unita: r.unita || "colli",
        prezzo_unitario: r.prezzo_unitario != null ? String(r.prezzo_unitario) : "",
        importo: r.importo != null ? String(r.importo) : "",
        iva: r.iva != null ? String(r.iva) : "",
        auto_campi: r.auto_campi || null,
        prezzo_atteso: r.prezzo_atteso != null ? r.prezzo_atteso : null,
        prezzo_anomalo: !!r.prezzo_anomalo,
      }));
      setRighe(parsed.length ? parsed : [{ ...emptyRiga }]);
      if (d.fornitore_id) {
        const fres = await api.get("/fornitori");
        setFornitori(fres.data);
        setFornitoreId(d.fornitore_id);
        toast.success(d.fornitore_creato
          ? `Fornitore "${d.fornitore_suggerito}" creato e selezionato`
          : `Fornitore "${d.fornitore_suggerito}" riconosciuto`);
      }
      if (d.duplicato) toast.warning("Attenzione: bolla con stesso numero e fornitore già registrata.");
      if (d.scanned) toast.warning("PDF scansionato: ho provato l'OCR. Controlla bene le righe.");
      else if (parsed.length) toast.success(`${parsed.length} righe estratte — verifica il totale`);
      else toast.warning("Nessuna riga rilevata. Inserisci manualmente.");
      const L = d.learning;
      if (L && (L.unita || L.iva || L.prezzo_atteso)) {
        toast.info(`Apprendimento applicato: ${L.unita} unità, ${L.iva} IVA completate · ${L.prezzo_atteso} prezzi attesi`);
      }
    } catch (err) { toast.error(apiError(err)); }
    setParsing(false);
  };

  const create = async (e) => {
    e.preventDefault();
    const validRighe = righe.filter((r) => r.prodotto && rowTotal(r) > 0);
    if (!numero || (!posteggioId && !fornitoreId) || validRighe.length === 0) {
      toast.error("Numero, posteggio o fornitore, e almeno una riga valida sono obbligatori");
      return;
    }
    try {
      const payload = {
        numero,
        data: data || null,
        posteggio_id: posteggioId || null,
        fornitore_id: fornitoreId || null,
        note,
        pdf_file_id: pdfFileId,
        righe: validRighe.map((r) => ({
          prodotto: r.prodotto,
          prodotto_originale: r.prodotto_originale || null,
          quantita: parseFloat(r.quantita) || 0,
          unita: r.unita,
          prezzo_unitario: parseFloat(r.prezzo_unitario) || 0,
          importo: r.importo !== "" && r.importo != null ? parseFloat(r.importo) : null,
          iva: r.iva !== "" && r.iva != null ? parseFloat(r.iva) : null,
        })),
      };
      if (editId) { await api.put(`/bolle/${editId}`, payload); toast.success("Bolla aggiornata"); }
      else { await api.post("/bolle", payload); toast.success("Bolla registrata"); }
      resetForm();
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/bolle/${id}`); toast.success("Bolla eliminata"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div className="animate-fade-up">
      <h1 className="font-display font-black text-4xl text-slate-50 tracking-tight mb-1">{text("bolle_title")}</h1>
      <p className="text-slate-400 mb-4">{text("bolle_subtitle")}</p>

      {learnStats && (
        <div data-testid="learning-chip" className="inline-flex items-center gap-2 mb-6 rounded-full bg-primary/10 border border-primary/30 px-3.5 py-1.5 text-xs text-primary">
          <Sparkles className="w-3.5 h-3.5" />
          <span>Apprendimento attivo — {learnStats.prodotti_appresi} prodotti, {learnStats.alias_appresi} correzioni, {learnStats.fornitori_appresi} fornitori appresi. Più registri, più diventa preciso.</span>
        </div>
      )}

      <datalist id="prodotti-list">
        {prodottiNomi.map((n, i) => <option key={`${n}-${i}`} value={n} />)}
      </datalist>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
        <form onSubmit={create} data-testid="bolla-form" className="xl:col-span-2 bg-slate-900/50 border border-slate-800 rounded-md p-5">
          <label data-testid="pdf-upload-label" className="flex items-center gap-3 border border-dashed border-primary/50 bg-primary/5 rounded-md px-4 py-3 mb-4 cursor-pointer hover:bg-primary/10 transition-colors">
            {parsing ? <Loader2 className="w-5 h-5 text-primary animate-spin" /> : <FileUp className="w-5 h-5 text-primary" />}
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-100">{parsing ? "Lettura PDF in corso…" : "Carica il PDF della bolla"}</div>
              <div className="text-xs text-slate-400">Estraggo articoli, quantità, prezzi, IVA, data e totale. Poi controlli.</div>
            </div>
            {pdfFileId && !parsing && <FileText className="w-5 h-5 text-emerald-400" />}
            <input data-testid="pdf-upload-input" type="file" accept="application/pdf" className="hidden" onChange={(e) => parsePdf(e.target.files?.[0])} />
          </label>

          <BollaMeta meta={meta} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
            <input data-testid="bolla-numero" placeholder="Numero bolla / DDT" value={numero} onChange={(e) => setNumero(e.target.value)} className={input} />
            <input data-testid="bolla-data" type="date" value={data} onChange={(e) => setData(e.target.value)} className={input} />
            <div>
              <select data-testid="bolla-posteggio" value={posteggioId} onChange={(e) => setPosteggioId(e.target.value)} className={input}>
                <option value="">Posteggio (mercato)…</option>
                {posteggi.map((p) => <option key={p.id} value={p.id}>{p.nome}{p.fornitore ? ` — ${p.fornitore}` : ""}</option>)}
              </select>
              <button type="button" data-testid="quick-add-posteggio" onClick={() => setQuickPosteggio(quickPosteggio ? null : { nome: "", settore: "" })} className="mt-1 text-xs text-primary hover:underline flex items-center gap-1">
                <Store className="w-3 h-3" /> {quickPosteggio ? "Annulla" : "Nuovo posteggio"}
              </button>
              {quickPosteggio && (
                <div data-testid="quick-posteggio-form" className="mt-2 flex gap-2 items-center bg-slate-800/60 border border-slate-700 rounded-md p-2">
                  <input data-testid="quick-posteggio-nome" autoFocus placeholder="Nome posteggio" value={quickPosteggio.nome} onChange={(e) => setQuickPosteggio({ ...quickPosteggio, nome: e.target.value })} className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-100 outline-none focus:border-primary" />
                  <button type="button" data-testid="quick-posteggio-save" onClick={createPosteggio} className="bg-primary text-slate-950 font-semibold rounded px-2.5 py-1.5 text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Crea</button>
                </div>
              )}
            </div>
            <div>
              <select data-testid="bolla-fornitore" value={fornitoreId} onChange={(e) => setFornitoreId(e.target.value)} className={input}>
                <option value="">Fornitore (opz.)…</option>
                {fornitori.map((f) => <option key={f.id} value={f.id}>{f.nome}{f.tipo === "fuori_mercato" ? " (fuori mercato)" : ""}</option>)}
              </select>
              <button type="button" data-testid="quick-add-fornitore" onClick={() => setQuickFornitore(quickFornitore ? null : { nome: "", tipo: "mercato" })} className="mt-1 text-xs text-primary hover:underline flex items-center gap-1">
                <Building2 className="w-3 h-3" /> {quickFornitore ? "Annulla" : "Nuovo fornitore"}
              </button>
              {quickFornitore && (
                <div data-testid="quick-fornitore-form" className="mt-2 flex gap-2 items-center bg-slate-800/60 border border-slate-700 rounded-md p-2">
                  <input data-testid="quick-fornitore-nome" autoFocus placeholder="Nome fornitore" value={quickFornitore.nome} onChange={(e) => setQuickFornitore({ ...quickFornitore, nome: e.target.value })} className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-slate-100 outline-none focus:border-primary" />
                  <select data-testid="quick-fornitore-tipo" value={quickFornitore.tipo} onChange={(e) => setQuickFornitore({ ...quickFornitore, tipo: e.target.value })} className="bg-slate-800 border border-slate-700 rounded px-1 py-1.5 text-xs text-slate-100 outline-none">
                    <option value="mercato">mercato</option>
                    <option value="fuori_mercato">fuori mercato</option>
                  </select>
                  <button type="button" data-testid="quick-fornitore-save" onClick={createFornitore} className="bg-primary text-slate-950 font-semibold rounded px-2.5 py-1.5 text-sm flex items-center gap-1"><Check className="w-4 h-4" /> Crea</button>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-12 gap-2 px-1 mb-1 text-[10px] uppercase tracking-wider text-slate-500">
            <span className="col-span-12 md:col-span-3">Prodotto</span>
            <span className="hidden md:block md:col-span-3">Q.tà / unità</span>
            <span className="hidden md:block md:col-span-2">€ prezzo</span>
            <span className="hidden md:block md:col-span-1">IVA%</span>
            <span className="hidden md:block md:col-span-2">€ importo</span>
          </div>

          <div className="space-y-3 mb-3" data-testid="bolla-righe">
            {righe.map((r, i) => (
              <RigaRow
                key={i}
                r={r}
                i={i}
                coherent={rowCoherent(r)}
                anomaly={priceAnomaly(r)}
                onChange={(field, val) => setRiga(i, field, val)}
                onRemove={() => removeRiga(i)}
              />
            ))}
          </div>

          <button type="button" data-testid="add-riga-btn" onClick={addRiga} className="text-sm text-primary hover:underline flex items-center gap-1 mb-4">
            <Plus className="w-4 h-4" /> Aggiungi riga
          </button>

          <div className="flex flex-col md:flex-row md:items-end gap-3 justify-between border-t border-slate-800 pt-4">
            <input placeholder="Note (opzionale)" value={note} onChange={(e) => setNote(e.target.value)} className={`${input} md:max-w-xs`} />
            <div className="flex items-end gap-4">
              <div className="text-right text-sm">
                <div className="text-slate-400">Netto <span className="font-mono-plex text-slate-200">€ {totaleNetto.toFixed(2)}</span></div>
                <div className="text-slate-400">IVA <span className="font-mono-plex text-slate-200">€ {totaleIva.toFixed(2)}</span></div>
                <div className="text-xs uppercase tracking-wider text-slate-400 mt-1">Totale</div>
                <div data-testid="bolla-totale" className="font-display font-black text-2xl text-emerald-400 font-mono-plex">€ {totaleIvato.toFixed(2)}</div>
              </div>
              <button data-testid="bolla-submit" type="submit" className="bg-primary text-slate-950 font-semibold rounded-md px-5 py-2.5 flex items-center gap-2 hover:opacity-90 transition-opacity">
                {editId ? <Pencil className="w-4 h-4" /> : <ReceiptText className="w-4 h-4" />} {editId ? "Aggiorna" : "Registra"}
              </button>
              {editId && <button type="button" data-testid="bolla-cancel" onClick={resetForm} className="text-sm text-slate-400 hover:text-slate-200 flex items-center gap-1"><X className="w-3 h-3" /> Annulla</button>}
            </div>
          </div>
        </form>

        <div className="xl:col-span-1">
          {pdfFileId ? (
            <div className="sticky top-4 bg-slate-900/50 border border-slate-800 rounded-md p-3" data-testid="pdf-preview">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wider text-slate-400">Anteprima PDF</span>
                <a href={fileUrl(pdfFileId)} target="_blank" rel="noreferrer" className="text-primary text-xs hover:underline">Apri</a>
              </div>
              <iframe title="anteprima-bolla" src={fileUrl(pdfFileId)} className="w-full h-[520px] rounded bg-white" />
            </div>
          ) : (
            <div className="hidden xl:flex items-center justify-center h-full text-slate-600 text-sm border border-dashed border-slate-800 rounded-md p-6 text-center">
              Carica un PDF per vedere qui l'anteprima affiancata al form.
            </div>
          )}
        </div>
      </div>

      <div className="space-y-3" data-testid="bolle-list">
        {bolle.length === 0 && <p className="text-slate-500 text-sm">Nessuna bolla registrata.</p>}
        {bolle.map((b) => (
          <BollaCard key={b.id} b={b} onEdit={() => startEdit(b)} onRemove={() => remove(b.id)} />
        ))}
      </div>
    </div>
  );
}
